import { ObservableMap, runInAction } from 'mobx';
import {
  AddMomentInput,
  DeleteMomentInput,
  GetMomentQuery,
  PageInfo,
  UpdateMomentInput,
  DECEPTIO_TURN_EXTRACTOR,
} from '@clipr/lib';
import { MomentModel, MomentProps, Topic, SubTopic, Answer, Question, Moment } from './moment';
import { fromMoments, MomentQueryable } from './moments';
import { RedactionMomentType, RedactionMomentTypes } from './player';
import { AsyncResult } from '../core';
import { ApiVariables, ApiQueryOptions } from '../api/apiSchema';
import { SyncStatus } from './job';
import { TrackModel } from './track';
import { Store } from '../store/store';

// ---------------------------------------------------------------------------
// Generic utility for merging items into an ObservableMap.
// Deduplicates the pattern used across apiFetchMoments, apiFetchComments and
// apiFetchReactions.
// ---------------------------------------------------------------------------

export function mergeItemsIntoLookup<T>(
  items: T[],
  lookup: ObservableMap<string, T>,
  getId: (item: T) => string,
): void {
  for (const item of items) {
    lookup.set(getId(item), item);
  }
}

// ---------------------------------------------------------------------------
// Moment filter helpers — pure functions that operate on a sorted moment list
// ---------------------------------------------------------------------------

export function filterTranscriptMoments(moments: MomentModel[]): MomentModel[] {
  return moments.filter(m => m.clipType === 'Transcript');
}

export function filterTopicMoments(moments: MomentModel[]): Topic[] {
  return moments.filter(m => m.clipType === 'Topic') as Topic[];
}

export function filterSubtopicMoments(moments: MomentModel[]): SubTopic[] {
  return moments.filter(m => m.clipType === 'SubTopic') as SubTopic[];
}

export function filterRedactionMoments(moments: MomentModel[]): Moment[] {
  return moments.filter(m => Object.values(RedactionMomentTypes).includes(
    m.momentType as RedactionMomentType
  ));
}

export function filterGenericMoments(moments: MomentModel[]): MomentModel[] {
  return moments.filter(m => m.clipType === 'Moment' && m.extractor !== DECEPTIO_TURN_EXTRACTOR);
}

export function filterAnswerMoments(moments: MomentModel[]): MomentModel[] {
  return moments.filter(
    (m) => m.clipType === 'Moment' && m.momentType === 'Answer' && m.extractor !== DECEPTIO_TURN_EXTRACTOR
  );
}

export function filterDeceptioAnswerMoments(moments: MomentModel[]): MomentModel[] {
  return moments.filter(
    m => m.clipType === 'Moment' && m.momentType === 'Answer' && m.extractor === DECEPTIO_TURN_EXTRACTOR
  );
}

export function filterQuestionMoments(moments: MomentModel[]): MomentModel[] {
  return moments.filter(
    (m) => m.clipType === 'Moment' && m.momentType === 'Question'
  );
}

// ---------------------------------------------------------------------------
// Active track-filtered moment helpers
// ---------------------------------------------------------------------------

export function filterActiveTopics(
  topicMoments: Topic[],
  activeTopicTrack: TrackModel | null,
): Topic[] {
  if (!activeTopicTrack) return [];
  return topicMoments.filter(m => m.trackId === activeTopicTrack.id) as Topic[];
}

export function filterActiveSubtopics(
  subtopicMoments: SubTopic[],
  activeSubtopicTrack: TrackModel | null,
): SubTopic[] {
  if (!activeSubtopicTrack) return [];
  return subtopicMoments.filter(m => m.trackId === activeSubtopicTrack.id) as SubTopic[];
}

export function filterActiveAnswers(
  answerMoments: MomentModel[],
  activeTopicTrack: TrackModel | null,
): Answer[] {
  if (!activeTopicTrack) return [];
  return answerMoments.filter(
    (m) => m.trackId === activeTopicTrack.id
  ) as Answer[];
}

export function filterActiveQuestions(
  questionMoments: MomentModel[],
  activeSubtopicTrack: TrackModel | null,
): Question[] {
  if (!activeSubtopicTrack) return [];
  return questionMoments.filter(
    (m) => m.trackId === activeSubtopicTrack.id
  ) as Question[];
}

// ---------------------------------------------------------------------------
// Moment lookup helpers
// ---------------------------------------------------------------------------

export function sortedMoments(momentLookup: ObservableMap<string, MomentModel>): MomentModel[] {
  return [...momentLookup.values()]
    .sort((a, b) => a.startTime - b.startTime);
}

export function visibleMomentsFrom(moments: MomentModel[]): MomentModel[] {
  return moments.filter(mom => mom.isActuallyVisibleToConsumer);
}

// ---------------------------------------------------------------------------
// Moment data access helpers
// ---------------------------------------------------------------------------

export function hasMomentInLookup(momentLookup: ObservableMap<string, MomentModel>, id: string): boolean {
  return momentLookup.has(id);
}

export function getMomentFromLookup(momentLookup: ObservableMap<string, MomentModel>, id: string): MomentModel | null {
  return momentLookup.get(id) || null;
}

export function maybeGetMomentFromLookup(momentLookup: ObservableMap<string, MomentModel>, id?: string | null): MomentModel | null {
  if (!id) return null;
  return momentLookup.get(id) || null;
}

export function insertMomentIntoLookup(
  props: Partial<MomentProps>,
  momentLookup: ObservableMap<string, MomentModel>,
  store: Store,
): MomentModel {
  const moment = new MomentModel(props, store);
  momentLookup.set(moment.id, moment);
  return moment;
}

// ---------------------------------------------------------------------------
// Fetch moments args builder
// ---------------------------------------------------------------------------

export function buildFetchMomentsArgs(
  jobId: string,
  more: boolean,
  momentPageInfo: PageInfo | null,
): ApiVariables<'getMoments'> {
  const args: ApiVariables<'getMoments'> = { jobId };

  if (more && momentPageInfo?.endCursor)
    args.after = momentPageInfo.endCursor;

  return args;
}

// ---------------------------------------------------------------------------
// Interface describing the subset of JobModel needed by the API helpers,
// avoiding a direct import of JobModel and preventing circular imports.
// ---------------------------------------------------------------------------

export interface MomentApiContext {
  readonly id: string;
  readonly store: Store;
  momentLookup: ObservableMap<string, MomentModel>;
  momentPageInfo: PageInfo | null;
  momentQueryable: MomentQueryable | null;

  readonly moments: MomentModel[];
  setMomentsSyncStatus(status: SyncStatus): void;
  insertMoment(props: Partial<MomentProps>): MomentModel;
  fetchDependencies(opts?: ApiQueryOptions): AsyncResult<boolean>;
}

// ---------------------------------------------------------------------------
// API methods — standalone async functions that take a context
// ---------------------------------------------------------------------------

export async function apiFetchMoments(
  ctx: MomentApiContext,
  more: boolean = false,
  opts?: ApiQueryOptions,
): AsyncResult<boolean> {
  const api = ctx.store.api;
  const args = buildFetchMomentsArgs(ctx.id, more, ctx.momentPageInfo);
  const [momentsRes, err] = await api.getMoments(args, opts);
  if (err) {
    ctx.setMomentsSyncStatus(SyncStatus.Error);
    return [null, err];
  }

  const items = momentsRes?.getMoments.edges || [];

  runInAction(() => {
    if (!more)
      ctx.momentLookup.clear();
    items.forEach(edge => {
      const moment = edge.node;
      // @ts-expect-error -- insertMoment expects a MomentModel but moment comes from partial GraphQL response
      ctx.insertMoment(moment);
    });

    ctx.momentQueryable = fromMoments(ctx.moments);
    ctx.momentPageInfo = momentsRes?.getMoments?.pageInfo || null;
    ctx.setMomentsSyncStatus(SyncStatus.Fetched);
  });

  if (ctx.momentPageInfo?.hasNextPage)
    await apiFetchMoments(ctx, true, opts);

  return [true];
}

export async function apiFetchMoment(
  ctx: MomentApiContext,
  momentId: string,
  opts?: ApiQueryOptions,
): AsyncResult<GetMomentQuery> {
  const api = ctx.store.api;
  const args: ApiVariables<'getMoment'> = {
    jobId: ctx.id,
    momentId,
  };

  const [momentsRes, err] = await api.getMoment(args, opts);
  if (err) return [null, err];

  return [momentsRes, null];
}

export async function apiAddMoment(
  ctx: MomentApiContext,
  input: AddMomentInput,
  updateJob = true,
): AsyncResult<string> {
  const api = ctx.store.api;

  const [res, err] = await api.addMoment({ args: input });
  if (err) return [null, err];

  const momentId = res?.addMoment?.id;
  if (!momentId)
    return [null, new Error('Created moment ID was not returned')];

  if (updateJob) {
    const [, refErr] = await ctx.fetchDependencies();
    if (refErr) return [null, refErr];
  }

  return [momentId];
}

export async function apiUpdateMoment(
  ctx: MomentApiContext,
  input: UpdateMomentInput,
): AsyncResult<string> {
  const api = ctx.store.api;

  const [res, err] = await api.updateMoment({ args: input });
  if (err) return [null, err];

  const momentId = res?.updateMoment?.id;
  if (!momentId)
    return [null, new Error('Updated moment ID was not returned')];

  const [, refErr] = await ctx.fetchDependencies();
  if (refErr) return [null, refErr];

  return [momentId];
}

export async function apiUpdateMoments(
  ctx: MomentApiContext,
  input: UpdateMomentInput[],
): AsyncResult<boolean> {
  const api = ctx.store.api;

  const res = await Promise.all(input.map(async args => await api.updateMoment({ args })));

  const resArr = res.map((el) => {
    const [res, err] = el;

    if (err)
      return [null, err];

    const momentId = res?.updateMoment?.id;
    if (!momentId)
      return [null, new Error('Updated moment ID was not returned')];

    return [momentId];
  });

  const hasErrors = resArr.find(res => {
    const [, err] = res;
    return !!err;
  });

  if (hasErrors)
    return [null, new Error('Update batch contains errors')];

  const [, refErr] = await ctx.fetchDependencies();
  if (refErr) return [null, refErr];

  return [true];
}

export async function apiDeleteMoment(
  ctx: MomentApiContext,
  input: DeleteMomentInput,
): AsyncResult<string> {
  const api = ctx.store.api;

  const [res, err] = await api.deleteMoment(input);
  if (err) return [null, err];

  const momentId = res?.deleteMoment?.deletedId;
  if (!momentId)
    return [null, new Error('Deleted moment ID was not returned')];

  const [, refErr] = await ctx.fetchDependencies();
  if (refErr) return [null, refErr];

  return [momentId];
}

export async function apiDeleteMoments(
  ctx: MomentApiContext,
  input: DeleteMomentInput[],
): AsyncResult<boolean> {
  const api = ctx.store.api;

  const res = await Promise.all(input.map(async args => await api.deleteMoment(args)));

  const resArr = res.map((el) => {
    const [res, err] = el;

    if (err)
      return [null, err];

    const momentId = res?.deleteMoment?.deletedId;
    if (!momentId)
      return [null, new Error('Deleted moment ID was not returned')];

    return [momentId];
  });

  const hasErrors = resArr.find(res => {
    const [, err] = res;
    return !!err;
  });

  if (hasErrors)
    return [null, new Error('Delete batch contains errors')];

  const [, refErr] = await ctx.fetchDependencies();
  if (refErr) return [null, refErr];

  return [true];
}
